import React, { useRef, useState } from "react";
import { hapticTick, hapticTap } from "./haptics";

/**
 * Draggable circular leverage gauge.
 *
 * The arc sweeps −135°..+135° and is segmented green → amber → red as risk
 * climbs; a needle points at the current value with the numeric readout at the
 * centre. Drag anywhere on the dial to set leverage, drive it from the panel's
 * slider, or click the centre readout and type a value. Every route produces a
 * whole multiple — leverage has no fractional steps.
 *
 * Two looks, one behaviour:
 *
 *   `variant="cockpit"` (default) — a moulded Pokédex part. Hard black
 *   outlines, plastic bezel, a pointer needle. Marked `.ce-raw` so the dark
 *   theme leaves its casing colours alone, same as the 3D objects.
 *
 *   `variant="app"` — the normal trading UI. No casing: a soft token-driven
 *   track with the risk colour filling in behind a handle that matches the
 *   app's slider thumb. Reads the generic tokens, so it themes with everything
 *   else and must NOT carry `.ce-raw`.
 *
 * Only the arc's green/amber/red and the accent are shared between the two —
 * they are semantic and do not shift.
 *
 * @param {{value: number, max?: number, min?: number, onChange: Function, size?: number, variant?: "cockpit"|"app"}} props
 */
// Zone boundaries and their colours, in one place so the dial, the linear
// slider under it and anything else that wants to agree on "how risky is this"
// read from the same table rather than re-deriving the thresholds.
export const RISK_ZONES = [
  { upTo: 0.34, label: "Low", color: "#16B57F", rgb: "22, 181, 127" },
  { upTo: 0.67, label: "Med", color: "#E0A500", rgb: "224, 165, 0" },
  { upTo: Infinity, label: "High", color: "#E64646", rgb: "230, 70, 70" },
];

/** Fraction of the way from min to max, clamped. */
export const leverageT = (value, min, max) =>
  Math.max(0, Math.min(1, (value - min) / Math.max(0.0001, max - min)));

/** The risk zone a leverage value falls in. */
export const leverageRisk = (value, min = 1, max = 100) => {
  const t = leverageT(value, min, max);
  return RISK_ZONES.find((z) => t < z.upTo) || RISK_ZONES[RISK_ZONES.length - 1];
};

export function LeverageDial({ value, max = 100, min = 1, onChange, size = 190, variant = "cockpit" }) {
  const app = variant === "app";
  const ref = useRef(null);
  const span = Math.max(0.0001, max - min);
  const t = Math.max(0, Math.min(1, (value - min) / span));
  // Short labels: the badge lives inside the needle sweep, and "MEDIUM RISK"
  // was wide enough to collide with the pointer at low values.
  const zone = leverageRisk(value, min, max);
  const risk = zone.label;
  const rc = zone.color;
  // The app badge is a soft chip rather than a solid one, so it needs the same
  // hue at low alpha. rgb triples rather than 8-digit hex — Safari < 15 drops
  // the latter silently and the chip goes transparent.
  const rcSoft = zone.rgb;

  const cx = 100;
  const cy = 100;
  const r = 78;
  // The arc only sweeps −135°..+135°, so the bottom quarter of a square viewBox
  // is always empty. The cockpit keeps it — the bezel is a full circle. The app
  // variant has no bezel, so that quarter is just a hole in the card; cropping
  // the box to 172 trims it to a small margin under the handle's lowest reach.
  // Anything reading the dial's centre in *container* space has to go through
  // these fractions rather than assuming the middle.
  const vbH = app ? 172 : 200;
  const cxFrac = cx / 200;
  const cyFrac = cy / vbH;
  const clock = (tt) => -135 + tt * 270; // degrees, 0 = up, clockwise positive
  const pt = (a, rr = r) => {
    const rad = (a * Math.PI) / 180;
    return [cx + rr * Math.sin(rad), cy - rr * Math.cos(rad)];
  };
  const arc = (t0, t1) => {
    const [x0, y0] = pt(clock(t0));
    const [x1, y1] = pt(clock(t1));
    const large = (t1 - t0) * 270 > 180 ? 1 : 0;
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
  };

  // Needle rides the outer band only — at r*0.52 it ran straight through
  // the centre readout and collided with the risk label.
  const [nx, ny] = pt(clock(t), r - 4);
  const [n0x, n0y] = pt(clock(t), r * 0.74);
  // App variant rides a handle on the track itself instead of a needle.
  const [hx, hy] = pt(clock(t), r);

  // Last value a detent fired for, so dragging ticks once per integer
  // rather than once per pointermove event.
  const lastTick = useRef(value);

  const setFromEvent = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width * cxFrac);
    const dy = e.clientY - (rect.top + rect.height * cyFrac);
    let a = (Math.atan2(dx, -dy) * 180) / Math.PI; // 0 up, clockwise positive
    a = Math.max(-135, Math.min(135, a));
    const nt = (a + 135) / 270;
    const next = Math.max(min, Math.min(max, Math.round(min + nt * span)));
    if (next !== lastTick.current) {
      lastTick.current = next;
      hapticTick();
    }
    onChange(next);
  };

  const onDown = (e) => {
    e.preventDefault();
    hapticTap();
    setFromEvent(e);
    const move = (ev) => setFromEvent(ev);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  /**
   * Typing a value.
   *
   * The dial detents to whole multiples and the shortcuts under it are fixed
   * stops, so typing is the way to reach a value between them. Clicking the
   * readout turns it into a field; Enter or blur commits, Escape backs out.
   *
   * Leverage is whole numbers only — 3x, not 3.5x — so anything typed is
   * rounded to an integer on commit, and the field refuses the characters that
   * would build a decimal in the first place.
   */
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const beginEdit = (e) => {
    // Without this the dial's own pointer handler reads the click as a drag
    // and snaps leverage to wherever the readout happens to sit.
    e.stopPropagation();
    e.preventDefault();
    setDraft(String(value));
    setEditing(true);
  };

  const commitEdit = () => {
    setEditing(false);
    const n = parseFloat(String(draft).replace(",", "."));
    if (!Number.isFinite(n)) return;
    const rounded = Math.round(Math.max(min, Math.min(max, n)));
    if (rounded !== value) { hapticTap(); onChange(rounded); }
  };

  // A value can still arrive fractional from elsewhere (a restored draft, a
  // URL); show it as the whole number it will become rather than a decimal the
  // controls cannot produce.
  const displayValue = Math.round(value);

  return (
    <div
      ref={ref}
      onPointerDown={onDown}
      className={app ? "ce-lev-dial" : "ce-raw"}
      role="slider"
      aria-label="Leverage"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={`${displayValue}x, ${risk} risk`}
      tabIndex={0}
      onKeyDown={(e) => {
        // Page/Home/End so a keyboard user isn't pressing Arrow 99 times to
        // cross a 100x range.
        const step = (d) => { hapticTick(); onChange(Math.max(min, Math.min(max, value + d))); };
        const big = Math.max(1, Math.round((max - min) / 10));
        if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); step(1); }
        else if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); step(-1); }
        else if (e.key === "PageUp") { e.preventDefault(); step(big); }
        else if (e.key === "PageDown") { e.preventDefault(); step(-big); }
        else if (e.key === "Home") { e.preventDefault(); hapticTick(); onChange(min); }
        else if (e.key === "End") { e.preventDefault(); hapticTick(); onChange(max); }
      }}
      style={{
        width: size,
        height: app ? size * (vbH / 200) : size,
        position: "relative",
        margin: "0 auto",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
        // The cockpit dial keeps its outline suppressed — it is a device part
        // and lives behind its own focus treatment. The app dial is a real form
        // control on a real page, so it keeps a visible focus ring (see the
        // `.ce-lev-dial:focus-visible` rule alongside the slider styles).
        outline: app ? undefined : "none",
      }}
    >
      <svg viewBox={`0 0 200 ${vbH}`} style={{ width: "100%", height: "100%", display: "block", overflow: "visible" }}>
        {app ? (
          <>
            {/* No casing. A neutral track with the risk colour swept in behind a
                handle — the radial reading of the app's linear slider.

                An earlier pass laid all three risk zones under the track at low
                alpha. Two problems, both visible: round caps on adjacent
                segments overlap into blobs at every seam, and 18% amber over a
                dark surface is olive, not amber. The scale is carried instead by
                the fill colour (which changes as you drag) plus the boundary
                pips below, and the track stays one clean neutral sweep. */}
            <path d={arc(0, 1)} stroke="var(--surface-3)" strokeWidth="10" fill="none" strokeLinecap="round" />

            {/* Zone boundary pips, set outside the track so nothing overlaps the
                stroke. Each is tinted with the zone it opens. */}
            {RISK_ZONES.slice(0, -1).map((z) => {
              const [px0, py0] = pt(clock(z.upTo), r + 9);
              const [px1, py1] = pt(clock(z.upTo), r + 14);
              const passed = t >= z.upTo;
              return (
                <line
                  key={z.upTo}
                  x1={px0} y1={py0} x2={px1} y2={py1}
                  stroke={z.color}
                  strokeOpacity={passed ? 0.9 : 0.4}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  style={{ transition: "stroke-opacity .18s ease" }}
                />
              );
            })}

            {/* `t > 0.001` — an arc of zero length still paints a round cap, so
                at min leverage a dot appeared with nothing behind it. */}
            {t > 0.001 && (
              <path d={arc(0, t)} stroke={rc} strokeWidth="10" fill="none" strokeLinecap="round"
                style={{ transition: "stroke .18s ease" }} />
            )}

            {/* Handle: solid risk colour with a surface ring, the radial twin of
                the linear thumb below it. */}
            <circle cx={hx} cy={hy} r="9" fill={rc} stroke="var(--surface)" strokeWidth="3.5"
              style={{ transition: "all .18s cubic-bezier(.34,1.56,.64,1)" }} />
          </>
        ) : (
          <>
            {/* Flat offset drop — a filled disc behind the bezel, never a blur. */}
            <circle cx={cx + 4} cy={cy + 4} r="92" fill="rgba(0,0,0,0.28)" />

            {/* Bezel: moulded part, hard black outline. */}
            <circle cx={cx} cy={cy} r="92" fill="#f2f3f5" stroke="#1b1b1b" strokeWidth="3" />

            {/* Recessed face the gauge is set into. */}
            <circle cx={cx} cy={cy} r="70" fill="#e4e5e9" stroke="#1b1b1b" strokeWidth="2" />

            {/* Track, outlined. The black arc under each segment gives every part
                its casing outline without needing a stroke-on-stroke trick. */}
            <path d={arc(0, 1)} stroke="#1b1b1b" strokeWidth="19" fill="none" strokeLinecap="butt" />
            <path d={arc(0, 1)} stroke="#d8dade" strokeWidth="14" fill="none" strokeLinecap="butt" />

            {/* Risk segments — semantic colours the spec preserves. */}
            <path d={arc(0, 0.34)} stroke="#16B57F" strokeWidth="14" fill="none" strokeLinecap="butt" />
            <path d={arc(0.34, 0.67)} stroke="#E0A500" strokeWidth="14" fill="none" strokeLinecap="butt" />
            <path d={arc(0.67, 1)} stroke="#E64646" strokeWidth="14" fill="none" strokeLinecap="butt" />

            {/* Segment dividers, so the three zones read as separate mouldings. */}
            {[0, 0.34, 0.67, 1].map((seg) => {
              const [dx0, dy0] = pt(clock(seg), r - 7);
              const [dx1, dy1] = pt(clock(seg), r + 7);
              return <line key={seg} x1={dx0} y1={dy0} x2={dx1} y2={dy1} stroke="#1b1b1b" strokeWidth="3" />;
            })}

            {/* Ticks */}
            {Array.from({ length: 11 }).map((_, i) => {
              const [a0x, a0y] = pt(clock(i / 10), r + 10);
              const [a1x, a1y] = pt(clock(i / 10), r + 15);
              return <line key={i} x1={a0x} y1={a0y} x2={a1x} y2={a1y} stroke="#1b1b1b" strokeWidth="2" />;
            })}

            {/* Needle: black casing with the risk colour inset, like a moulded pointer. */}
            <line x1={n0x} y1={n0y} x2={nx} y2={ny} stroke="#1b1b1b" strokeWidth="9" strokeLinecap="round"
              style={{ transition: "all .18s cubic-bezier(.34,1.56,.64,1)" }} />
            <line x1={n0x} y1={n0y} x2={nx} y2={ny} stroke={rc} strokeWidth="4" strokeLinecap="round"
              style={{ transition: "all .18s cubic-bezier(.34,1.56,.64,1)" }} />
          </>
        )}
      </svg>

      {/* centre readout. The wrapper is inset:0, so a percentage translate is a
          percentage of the container — which is exactly the correction needed
          to move from the box's middle to the dial's centre after the crop. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          transform: `translateY(${((cyFrac - 0.5) * 100).toFixed(2)}%)`,
        }}
      >
        {/* Value reads as ink on the face, not as a coloured glyph — the risk
            colour belongs to the arc and the badge, which is where it means
            something. */}
        <div
          className={`ce-lev-type${app ? "" : " ce-num"}${editing ? " is-editing" : ""}`}
          onPointerDown={editing ? undefined : beginEdit}
          title={editing ? undefined : "Click to type a leverage value"}
          style={{
            fontFamily: app ? "General Sans" : undefined,
            fontSize: size * 0.26,
            fontWeight: 800,
            color: app ? "var(--ink)" : "var(--pd-ink, #1b1b1b)",
            lineHeight: 1,
            letterSpacing: "-0.03em",
            // The wrapper is `pointer-events: none` so drags pass through to
            // the dial. The readout takes its own back, so it can be clicked.
            pointerEvents: "auto",
            cursor: "text",
            display: "flex",
            alignItems: "baseline",
            // Nothing said the number was a field — the underline does, in the
            // same dashed idiom the rest of the app uses for editable-in-place
            // values, and the hover chip confirms it before the click.
            padding: "1px 8px 2px",
            borderRadius: 10,
            borderBottom: `2px dashed ${app ? "var(--ink-4)" : "var(--pd-ink-2, #5c5c5c)"}`,
          }}
        >
          {editing ? (
            <input
              autoFocus
              type="text"
              inputMode="numeric"
              value={draft}
              onChange={(e) => {
                // Digits and one separator only — a stray letter would blank
                // the field and drop leverage back to where it started.
                // Digits only: no decimal point, no comma.
                setDraft(e.target.value.replace(/[^0-9]/g, ""));
              }}
              onFocus={(e) => e.target.select()}
              onPointerDown={(e) => e.stopPropagation()}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                // The dial's own key handler steps leverage on every arrow
                // press; inside the field those belong to the caret.
                e.stopPropagation();
                if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                else if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
              }}
              aria-label="Leverage value"
              style={{
                width: `${Math.max(2, String(draft).length) + 0.5}ch`,
                maxWidth: size * 0.52,
                padding: 0,
                border: "none",
                borderBottom: `2px solid ${rc}`,
                background: "transparent",
                textAlign: "center",
                font: "inherit",
                fontSize: size * 0.26,
                fontWeight: 800,
                letterSpacing: "-0.03em",
                color: "inherit",
                outline: "none",
              }}
            />
          ) : (
            displayValue
          )}
          {/* The × is a unit, not a digit. In the app variant it drops to a
              lighter weight and gets a hair of leading space so it stops
              reading as part of the number. */}
          <span
            style={{
              fontSize: size * (app ? 0.115 : 0.13),
              fontWeight: app ? 600 : undefined,
              marginLeft: app ? size * 0.012 : undefined,
              letterSpacing: "normal",
              color: app ? "var(--ink-3)" : "var(--pd-ink-2, #5c5c5c)",
            }}
          >
            ×
          </span>
        </div>

        {/* Risk badge. Cockpit: a moulded chip, black-outlined like every other
            part. App: a soft tinted chip, the shape the rest of the UI uses. */}
        <div
          style={
            app
              ? {
                  marginTop: 6,
                  padding: "2px 9px",
                  background: `rgba(${rcSoft}, 0.14)`,
                  color: rc,
                  borderRadius: 999,
                  fontFamily: "General Sans",
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  whiteSpace: "nowrap",
                  maxWidth: "58%",
                }
              : {
                  marginTop: 5,
                  padding: "2px 8px",
                  background: rc,
                  color: "#ffffff",
                  border: "2px solid var(--pd-black, #1b1b1b)",
                  borderRadius: 999,
                  boxShadow: "2px 2px 0 rgba(0,0,0,0.28)",
                  fontSize: 9.5,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  whiteSpace: "nowrap",
                  maxWidth: "58%",
                }
          }
        >
          {risk}
        </div>
      </div>
    </div>
  );
}

export default LeverageDial;
